import { useMemo, useState, type ComponentProps } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Pagination } from '../../ui/pagination';

import {
  FileText,
  RefreshCw,
  Eye,
  CircleDollarSign,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Pencil,
  Search,
} from 'lucide-react';

import { useClientPagination } from '@/hooks/useClientPagination';
import { formatCurrency } from '@/utils/formatters';
import type { BatchInvestorNotice, DrawdownPayment } from '@/types/drawdown';

interface InvestorsTableProps {
  investorNotices: BatchInvestorNotice[];
  paymentsByInvestorId: Record<string, DrawdownPayment[]>;
  onDownloadPdf: (notificationId: string) => Promise<void>;
  onPreviewNotification: (investorId: string) => Promise<void>;
  onViewPayments: (investor: BatchInvestorNotice) => void;
  onRecordPayment?: (investor: BatchInvestorNotice) => void;
  onEditAmount?: (investor: BatchInvestorNotice) => void;
}

type SortKey =
  'investor' | 'commitment' | 'amount_due' | 'reconciled' | 'status';
type SortDirection = 'asc' | 'desc';

// Payment progression order so "sort by status" groups unpaid investors first
const STATUS_SORT_RANK: Record<string, number> = {
  pending: 0,
  partial: 1,
  paid: 2,
  exempted: 3,
};

const STATUS_BADGE_VARIANT: Record<
  string,
  ComponentProps<typeof Badge>['variant']
> = {
  paid: 'default',
  partial: 'secondary',
  exempted: 'outline',
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  paid: 'bg-green-100 text-green-800 hover:bg-green-100',
  partial: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
};

type HeadAlign = 'left' | 'right' | 'center';

const HEAD_ALIGN_CLASS: Record<HeadAlign, string> = {
  left: '',
  right: 'text-right',
  center: 'text-center',
};

function SortIcon({
  isActive,
  direction,
}: {
  isActive: boolean;
  direction: SortDirection;
}) {
  if (!isActive) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
  if (direction === 'asc') return <ArrowUp className="h-3 w-3" />;
  return <ArrowDown className="h-3 w-3" />;
}

const SORT_VALUE_GETTERS: Record<
  SortKey,
  (notice: BatchInvestorNotice) => string | number
> = {
  investor: (notice) => notice.investor_name.toLowerCase(),
  commitment: (notice) => notice.commitment_amount,
  amount_due: (notice) => notice.amount_due,
  reconciled: (notice) => notice.reconciled_amount,
  status: (notice) => STATUS_SORT_RANK[notice.status] ?? 99,
};

export function InvestorsTabel({
  investorNotices,
  paymentsByInvestorId,
  onDownloadPdf,
  onPreviewNotification,
  onViewPayments,
  onRecordPayment,
  onEditAmount,
}: InvestorsTableProps) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const visibleNotices = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    const filtered = investorNotices.filter(
      (notice) =>
        (statusFilter === 'all' || notice.status === statusFilter) &&
        (!searchTerm || notice.investor_name.toLowerCase().includes(searchTerm))
    );
    if (!sortKey) return filtered;

    const getValue = SORT_VALUE_GETTERS[sortKey];
    return [...filtered].sort((a, b) => {
      const aValue = getValue(a);
      const bValue = getValue(b);
      const comparison =
        typeof aValue === 'string'
          ? aValue.localeCompare(bValue as string)
          : (aValue as number) - (bValue as number);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [investorNotices, search, statusFilter, sortKey, sortDirection]);

  const {
    pageItems: paginatedNotices,
    paginationProps,
    resetPage,
  } = useClientPagination(visibleNotices, 20);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const renderSortableHead = (
    key: SortKey,
    label: string,
    align: HeadAlign = 'left'
  ) => (
    <TableHead className={HEAD_ALIGN_CLASS[align]}>
      <button
        type="button"
        onClick={() => toggleSort(key)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${
          align === 'right' ? 'flex-row-reverse' : ''
        }`}
      >
        {label}
        <SortIcon isActive={sortKey === key} direction={sortDirection} />
      </button>
    </TableHead>
  );

  const handleDownload = async (notificationId: string) => {
    try {
      setDownloadingId(notificationId);
      await onDownloadPdf(notificationId);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePreview = async (investorId: string) => {
    try {
      setPreviewingId(investorId);
      await onPreviewNotification(investorId);
    } catch (error) {
      console.error('Failed to preview notification:', error);
    } finally {
      setPreviewingId(null);
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search investor..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
            className="w-64 h-8 pl-8"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(value);
            resetPage();
          }}
        >
          <SelectTrigger className="w-32 h-8">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="exempted">Exempted</SelectItem>
          </SelectContent>
        </Select>
        {(search || statusFilter !== 'all') && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={() => {
                setSearch('');
                setStatusFilter('all');
                resetPage();
              }}
            >
              Clear
            </Button>
            <span className="text-sm text-muted-foreground">
              {visibleNotices.length} of {investorNotices.length} investors
            </span>
          </>
        )}
      </div>
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              {renderSortableHead('investor', 'Investor')}
              {renderSortableHead('commitment', 'Commitment', 'right')}
              <TableHead className="text-right">Call %</TableHead>
              {renderSortableHead('amount_due', 'Amount Due', 'right')}
              <TableHead>Payment Ref</TableHead>
              <TableHead>Investor IBAN</TableHead>
              {renderSortableHead('reconciled', 'Reconciled', 'right')}
              <TableHead className="text-center">Verification</TableHead>
              {renderSortableHead('status', 'Status', 'center')}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleNotices.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  className="text-center text-muted-foreground py-8"
                >
                  {investorNotices.length === 0
                    ? 'No investor notices found for this batch'
                    : 'No investors match the current filters'}
                </TableCell>
              </TableRow>
            ) : (
              paginatedNotices.map((notice) => (
                <TableRow key={notice.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {notice.investor_name}
                      {notice.is_fund_investor && (
                        <Badge
                          variant="outline"
                          className="text-xs"
                          title="A fund investing in this one; it calls its own investors to pay"
                        >
                          Fund
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(notice.commitment_amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    {notice.drawdown_percentage.toFixed(2)}%
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(notice.amount_due)}
                    {notice.net_amount_due < 0 && (
                      <div className="text-xs font-normal text-muted-foreground">
                        Refund owed {formatCurrency(-notice.net_amount_due)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {notice.reference_number ? (
                      <span className="font-mono text-xs">
                        {notice.reference_number}
                      </span>
                    ) : (
                      <Badge variant="destructive">Missing</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {notice.investor_iban ? (
                      <span className="font-mono text-xs">
                        {notice.investor_iban}
                      </span>
                    ) : (
                      <Badge variant="destructive">Missing</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(notice.reconciled_amount)}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant={
                        notice.verification_status === 'posted'
                          ? 'default'
                          : 'secondary'
                      }
                      className={
                        notice.verification_status === 'posted'
                          ? 'bg-green-100 text-green-800 hover:bg-green-100'
                          : ''
                      }
                    >
                      {notice.verification_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant={
                        STATUS_BADGE_VARIANT[notice.status] ?? 'destructive'
                      }
                      className={STATUS_BADGE_CLASS[notice.status] ?? ''}
                    >
                      {notice.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Record Payment */}
                      {onRecordPayment &&
                        notice.status !== 'paid' &&
                        notice.status !== 'exempted' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => onRecordPayment(notice)}
                            title="Record Payment"
                          >
                            <CircleDollarSign className="h-4 w-4" />
                          </Button>
                        )}
                      {onEditAmount && isAmountEditable(notice) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => onEditAmount(notice)}
                          title="Edit amount"
                          aria-label={`Edit amount for ${notice.investor_name}`}
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        </Button>
                      )}
                      {/* Bank transactions credited to this investor */}
                      {(paymentsByInvestorId[notice.investor_id] ?? []).length >
                        0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => onViewPayments(notice)}
                          title="View bank transactions"
                          aria-label={`View bank transactions for ${notice.investor_name}`}
                        >
                          <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        </Button>
                      )}
                      {/* Notice PDF: the generated one, or a draft preview */}
                      {noticeIsGenerated(notice) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() =>
                            handleDownload(notice.notification_id!)
                          }
                          disabled={downloadingId === notice.notification_id}
                          title="Download PDF"
                        >
                          {downloadingId === notice.notification_id ? (
                            <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                          ) : (
                            <FileText className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                          )}
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handlePreview(notice.investor_id)}
                          disabled={previewingId === notice.investor_id}
                          title="Preview notice (draft)"
                        >
                          {previewingId === notice.investor_id ? (
                            <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                          ) : (
                            <FileText className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                          )}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {visibleNotices.length > 0 && <Pagination {...paginationProps} />}
    </div>
  );
}

/** A notice whose PDF is on file, ready to download rather than preview. */
function noticeIsGenerated(notice: BatchInvestorNotice): boolean {
  return (
    notice.pdf_generated &&
    notice.document_id != null &&
    notice.notification_id != null
  );
}

/** The server refuses exempted notices and those the prepayment pool funds. */
function isAmountEditable(notice: BatchInvestorNotice): boolean {
  return (
    notice.status !== 'exempted' &&
    notice.prepayment_used === 0 &&
    notice.amount_due === notice.drawdown_amount
  );
}
